import { Step } from 'prosemirror-transform';
import { collab, getVersion, receiveTransaction } from 'prosemirror-collab';
import { Plugin, PluginKey } from 'prosemirror-state';
import {
  GetInitialDocumentContentAndVersion,
  ListenToDocumentUpdates,
  SaveDocumentContent,
  SendDocumentUpdateSteps,
} from './types';
import { debounce } from '@livekatsomo/shared/utils';
import { onUpdate } from './onUpdate';

export interface CustomPluginState extends PluginConfig {
  initialized: boolean;
  initializing: boolean;
}

export interface PluginConfig {
  clientID: string;
  debounceTime?: number;
  listenToDocumentUpdates: ListenToDocumentUpdates;
  sendDocumentUpdateSteps: SendDocumentUpdateSteps;
  saveDocumentContent: SaveDocumentContent;
  getInitialDocumentContent: GetInitialDocumentContentAndVersion;
  versionListener?: (version: number) => void;
}

export const pluginKey = new PluginKey<CustomPluginState>(
  'firestore-collaboration-plugin',
);

/**
 * Creates a ProseMirror plugin for Firestore collaboration.
 *
 * @param config - The configuration object for the plugin.
 * @returns A ProseMirror plugin for Firestore collaboration.
 */
export function firestoreCollaborationPlugin(
  config: PluginConfig,
): Plugin<CustomPluginState> {
  return new Plugin({
    key: pluginKey,
    state: {
      init: () => {
        const pluginState: CustomPluginState = {
          ...config,
          initialized: false,
          initializing: false,
          sendDocumentUpdateSteps: config.debounceTime
            ? debounce(config.sendDocumentUpdateSteps, config.debounceTime)
            : config.sendDocumentUpdateSteps,
        };
        return pluginState;
      },
      apply: (tr, value) => {
        if (!value.initializing) {
          const initializing = tr.getMeta('initializing');
          if (initializing) {
            return {
              ...value,
              initializing,
            };
          }
        }
        if (!value.initialized) {
          const initialized = tr.getMeta('initialized');
          if (initialized) {
            return {
              ...value,
              initialized,
            };
          }
        }
        return value;
      },
    },
    filterTransaction(tr, state) {
      const pluginState = pluginKey.getState(state);
      const collabInitialiseTr = tr.getMeta('initialized');

      // Don't allow transactions that modifies the document before
      // plugin is ready.
      if (!!collabInitialiseTr && !pluginState?.initialized && tr.docChanged) {
        return false;
      }

      return true;
    },
    view: (editorView) => {
      const pluginState = pluginKey.getState(editorView.state);
      if (!pluginState) {
        throw new Error('Plugin state is not defined');
      }
      const { clientID, getInitialDocumentContent } = pluginState;

      let unsubscribe: () => void;

      if (!pluginState.initialized && !pluginState.initializing) {
        editorView.dispatch(editorView.state.tr.setMeta('initializing', true));
        getInitialDocumentContent().then(({ version, content }) => {
          const newState = editorView.state.reconfigure({
            plugins: [
              collab({ clientID: clientID, version }),
              ...editorView.state.plugins,
            ],
          });
          if (content)
            newState.doc = editorView.state.schema.nodeFromJSON(content);
          // If editor is destroyed before initialisation is done, don't try to update state
          if (editorView.isDestroyed) return;
          editorView.updateState(newState);
          editorView.dispatch(newState.tr.setMeta('initialized', true));
        });
      }

      return {
        update: (view, prevState) => {
          const prevPluginState = pluginKey.getState(prevState);
          const pluginState = pluginKey.getState(editorView.state);
          if (!pluginState) {
            throw new Error('Plugin state is not defined');
          }
          const { listenToDocumentUpdates, initialized } = pluginState;
          if (prevPluginState?.initialized !== pluginState.initialized) {
            // Plugin is initialized
            unsubscribe = listenToDocumentUpdates({
              version: getVersion(editorView.state),
              onUpdate: (changes) => {
                const steps: Step[] = [];
                const clientIds: string[] = [];
                changes.forEach((change) => {
                  change.steps.forEach((step) => {
                    steps.push(Step.fromJSON(editorView.state.schema, step));
                    clientIds.push(change.clientID.toString());
                  });
                });

                editorView.dispatch(
                  receiveTransaction(editorView.state, steps, clientIds),
                );
              },
              onError: (error) => {
                throw error;
              },
            });
          }
          if (initialized) onUpdate(view);
        },
        destroy: () => {
          const { saveDocumentContent } = pluginState;
          unsubscribe && unsubscribe();
          if (!pluginState.initialized) return;
          const state = editorView.state.apply(editorView.state.tr);
          saveDocumentContent(state.doc.toJSON(), getVersion(state));
        },
      };
    },
  });
}
